import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@/shared/ui/checkbox';
import { Icon } from '@/shared/ui/icon';

import { FilterValueEditor } from '../editors/FilterValueEditor';
import { blankFilter, isFilterActive } from '../model/filterModel';
import type { ListFilterFieldDef } from '../model/types';
import { PANEL_WIDTH_DEFAULT, type ListPageController } from '../model/useListPageController';

const PANEL_WIDTH_MIN = 200;
const PANEL_WIDTH_MAX = 480;

function FieldRow<TItem>({
  def,
  controller,
}: {
  def: ListFilterFieldDef;
  controller: ListPageController<TItem>;
}) {
  const { t } = useTranslation('listPage');
  const value = controller.filters.find((f) => f.field === def.key);
  const active = value !== undefined;
  const pinned = Boolean(value?.pinned);
  const filled = value ? isFilterActive(def, value) : false;

  // Ruční rozbalení prázdného filtru (přes checkbox). Vyplněný filtr je rozbalený vždy;
  // přišpendlení (po načtení / špendlíkem) rozbalení neřídí.
  const [expanded, setExpanded] = useState(false);

  // Filtr opustil stav (např. hromadné Vymazat) → zapomenout ruční rozbalení.
  useEffect(() => {
    if (!active) {
      setExpanded(false);
    }
  }, [active]);

  const open = active && (filled || expanded);

  // Checkbox = rozbalit/sbalit editor. Rozbalení přidá prázdný filtr (chip nahoře), sbalení
  // filtr resetuje (připnutý vyprázdní, nepřipnutý odebere — `clearFilter`).
  const toggleOpen = (next: boolean) => {
    if (next) {
      if (!active) {
        controller.upsertFilter(blankFilter(def));
      }
      setExpanded(true);
    } else {
      setExpanded(false);
      controller.clearFilter(def.key);
    }
  };

  return (
    <div className="phxlist-fside-field" data-open={open || undefined}>
      <div className="phxlist-fside-fieldhead">
        <Checkbox
          checked={open}
          onCheckedChange={toggleOpen}
          label={def.label}
          className="min-w-0 flex-1"
        />
        <button
          type="button"
          className="phxlist-pinbtn phxlist-fside-pin"
          data-on={pinned || undefined}
          title={pinned ? t('chips.unpin') : t('chips.pin')}
          onClick={() => controller.togglePin(def.key)}
        >
          <Icon name="pin" size={11} />
        </button>
      </div>
      {open && value ? (
        <div className="phxlist-fside-editor">
          <FilterValueEditor
            def={def}
            value={value}
            onChange={(next) => controller.upsertFilter({ ...next, pinned: value.pinned })}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Panel filtrů (anatomie 01 §4) — katalog všech polí šablony po skupinách; checkbox aktivuje
 * filtr, editor se rozbalí inline; pravá hrana je splitter (200–480 px, dvojklik reset).
 * Panel i chipy editují tentýž stav filtrů.
 */
export function FilterPanel<TItem>({
  controller,
  groups,
  fields,
}: {
  controller: ListPageController<TItem>;
  groups: { id: string; label: string }[];
  fields: ListFilterFieldDef[];
}) {
  const { t } = useTranslation('listPage');
  // Šířka panelu je serverová preference (anatomie 05 §6 + frontend.04) — drží ji controller,
  // persistenci řeší usePersistUserState.
  const width = controller.panelWidth;
  const [resizing, setResizing] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;

  const persistWidth = useCallback((next: number) => controller.setPanelWidth(next), [controller]);

  const startResize = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = widthRef.current;
    setResizing(true);
    const onMove = (move: MouseEvent) => {
      const next = Math.round(
        Math.min(PANEL_WIDTH_MAX, Math.max(PANEL_WIDTH_MIN, startWidth + move.clientX - startX))
      );
      persistWidth(next);
    };
    const onUp = () => {
      setResizing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  if (!controller.panelOpen) {
    return null;
  }

  return (
    <aside className="phxlist-fside" style={{ width }} data-resizing={resizing || undefined}>
      <div className="phxlist-fside-head">
        <span className="phxlist-caps text-phx-text-2">{t('panel.title')}</span>
        <div className="gap-phx-1 ml-auto flex">
          {controller.filters.length > 0 ? (
            <button
              type="button"
              className="phxlist-btn"
              data-variant="ghost"
              onClick={controller.clearAllFilters}
            >
              {t('panel.clear')}
            </button>
          ) : null}
          <button
            type="button"
            className="phxlist-btn"
            data-variant="ghost"
            data-icon
            title={t('panel.close')}
            onClick={() => controller.setPanelOpen(false)}
          >
            <Icon name="x" size={13} />
          </button>
        </div>
      </div>
      <div className="phxlist-fside-body">
        {groups.map((group) => {
          const inGroup = fields.filter((f) => f.group === group.id);
          if (inGroup.length === 0) {
            return null;
          }
          return (
            <div key={group.id}>
              <div className="phxlist-caps text-phx-text-muted px-phx-1 pt-phx-4 pb-phx-2">
                {group.label}
              </div>
              {inGroup.map((def) => (
                <FieldRow key={def.key} def={def} controller={controller} />
              ))}
            </div>
          );
        })}
      </div>
      <div
        className="phxlist-fside-splitter"
        role="separator"
        aria-orientation="vertical"
        title={t('panel.splitter')}
        onMouseDown={startResize}
        onDoubleClick={() => persistWidth(PANEL_WIDTH_DEFAULT)}
      />
    </aside>
  );
}
